export const ADMIN_PERMISSIONS = Object.freeze({
    DASHBOARD_VIEW: "page.dashboard.view",
    USER_VIEW: "page.users.view",
    SUPPORT_REQUESTS_VIEW: "page.support-requests.view",
    CONTENT_MANAGEMENT_VIEW: "page.content-management.view",
    WORKSPACE_VIEW: "page.workspaces.view",
    OPERATIONS_ACCESS: "page.operations.view",
    SALES_ACCESS: "page.sales.view",
    FINANCE_ACCESS: "operations.tab.finance",
    AI_ACCESS: "page.ai.view",
    SETTINGS_ACCESS: "page.settings.view",
    PLATFORM_SERVICES_ACCESS: "page.platform-services.view",
    PLATFORM_SERVICES_MANAGE: "platform-services.manage",

    USER_TAB_PROFILE: "users.tab.profile",
    USER_TAB_WORKSPACES: "users.tab.workspaces",
    USER_TAB_ACTIVITY: "users.tab.activity",
    USER_TAB_FINANCE: "users.tab.finance",
    USER_TAB_SECURITY: "users.tab.security",
    USER_UPDATE: "users.action.update",
    USER_SECURITY_ACTIONS: "users.action.security",
    USER_SESSION_ACTIONS: "users.action.sessions",
    USER_PASSWORD_RECOVERY: "users.action.password-recovery",
    USER_INTERNAL_NOTE: "users.action.note",
    USER_OTP_CONTENT: "users.content.otp",

    WORKSPACE_TAB_SETTINGS: "workspaces.tab.settings",
    WORKSPACE_TAB_MEMBERS: "workspaces.tab.members",
    WORKSPACE_TAB_BILLING: "workspaces.tab.billing",
    WORKSPACE_TAB_AI: "workspaces.tab.ai",
    WORKSPACE_TAB_HEALTH: "workspaces.tab.health",
    WORKSPACE_TAB_AUDIT: "workspaces.tab.audit",
    WORKSPACE_UPDATE_SETTINGS: "workspaces.action.update-settings",
    WORKSPACE_MANAGE_MEMBERS: "workspaces.action.manage-members",
    WORKSPACE_MANAGE_ROLES: "workspaces.action.manage-roles",
    WORKSPACE_SET_OWNER: "workspaces.action.set-owner",
    WORKSPACE_UPDATE_AI: "workspaces.action.update-ai",
    WORKSPACE_REPAIR: "workspaces.action.repair",
    WORKSPACE_AUDIT_DETAILS: "workspaces.content.audit-details",

    OPERATIONS_TAB_OVERVIEW: "operations.tab.overview",
    OPERATIONS_TAB_HEALTH: "operations.tab.health",
    OPERATIONS_TAB_FINANCE: "operations.tab.finance",
    OPERATIONS_TAB_AI: "operations.tab.ai",
    OPERATIONS_TAB_ACTIVITY: "operations.tab.activity",

    AI_CONFIGURE: "ai.action.configure",
});

function normalizeToken(value) {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[_\s]+/g, "-");
}

function normalizePermission(value) {
    return String(value || "").trim().toLowerCase();
}

function asArray(value) {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.results)) return value.results;
    return value ? [value] : [];
}

export function getAdminRoleTokens(user) {
    const roles = asArray(user?.roles?.data || user?.roles?.results || user?.roles || user?.role);
    return [...new Set(roles.flatMap((role) => {
        if (typeof role === "string") return [normalizeToken(role)];
        return [role?.code, role?.name, role?.type, role?.slug]
            .filter(Boolean)
            .map(normalizeToken);
    }).filter(Boolean))];
}

export function hasAnyAdminRole(user, allowedRoles = []) {
    const roleTokens = new Set(getAdminRoleTokens(user));
    return allowedRoles.some((role) => roleTokens.has(normalizeToken(role)));
}

export function hasAdminRoleAccess(user, allowedRoles = []) {
    return isSuperAdmin(user) || hasAnyAdminRole(user, allowedRoles);
}

export function isSuperAdmin(user) {
    return getAdminRoleTokens(user).some((token) => [
        "strapi-super-admin",
        "super-admin",
        "superadmin",
    ].includes(token));
}

export function getAdminPermissions(user) {
    if (!user) return [];
    if (isSuperAdmin(user)) return ["*"];
    return [...new Set(asArray(user.permissions).map((permission) => (
        normalizePermission(typeof permission === "string"
            ? permission
            : permission?.code || permission?.permission || permission?.name)
    )).filter(Boolean))];
}

function permissionMatches(granted, requested) {
    if (granted === "*" || granted === requested) return true;
    if (!granted.endsWith(".*")) return false;
    return requested.startsWith(granted.slice(0, -1));
}

export function hasAdminPermission(user, permission) {
    if (!user || !permission) return false;
    const requested = normalizePermission(permission);
    return getAdminPermissions(user).some((granted) => permissionMatches(granted, requested));
}

export function hasAnyAdminPermission(user, permissions = []) {
    return permissions.some((permission) => hasAdminPermission(user, permission));
}

export function hasSalesAccess(user) {
    return hasAdminPermission(user, ADMIN_PERMISSIONS.SALES_ACCESS);
}

export function hasFinanceAccess(user) {
    return hasAdminPermission(user, ADMIN_PERMISSIONS.OPERATIONS_TAB_FINANCE);
}

export function hasAiAccess(user) {
    return hasAdminPermission(user, ADMIN_PERMISSIONS.AI_ACCESS);
}

export function hasOperationsAccess(user) {
    return hasAdminPermission(user, ADMIN_PERMISSIONS.OPERATIONS_ACCESS);
}

export function hasPlatformServicesAccess(user) {
    return hasAdminPermission(user, ADMIN_PERMISSIONS.PLATFORM_SERVICES_ACCESS);
}

export function getAdminRoleLabels(user) {
    return getAdminRoleTokens(user);
}
